import { Stack } from '@fluentui/react';
import React, { useCallback } from 'react';
import { useStyles } from './index.styles';
import {
  MessagesListsProps,
  MessagesSections,
  PivotKeys,
} from 'common/components/MessagesSections';
import { NetworkStatus, useQuery } from '@apollo/client';
import {
  TransactionSigningMessage,
  TransactionSigningMessageVariables,
} from './__generated__/TransactionSigningMessage';
import { NewMessage } from 'ap/signing/components/NewMessage';

import GET_TRANSACTION_MESSAGE from './TransactionSigningMessage.graphql';
interface TransactionSigningMessageProps
  extends Pick<MessagesListsProps, 'userMessageCounts'> {
  invoiceID: string;
  onCreateMessage: () => void;
}

export const Messages: React.FC<TransactionSigningMessageProps> = ({
  invoiceID,
  userMessageCounts,
  onCreateMessage,
}) => {
  const styles = useStyles();
  const [selectedKey, setSelectedKey] = React.useState<string>(
    PivotKeys.received
  );

  const {
    loading: loadingMessages,
    data: messagesData,
    variables: messageListVariables,
    fetchMore,
    refetch,
    networkStatus,
  } = useQuery<TransactionSigningMessage, TransactionSigningMessageVariables>(
    GET_TRANSACTION_MESSAGE,
    {
      variables: {
        id: invoiceID,
        first: 10,
        filter: { _isIncomingMessage: { equalTo: true } },
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );

  const onListChange = useCallback(
    async (listName?: string) => {
      if (listName) setSelectedKey(listName);
      const _isOutgoingMessage = { equalTo: listName === PivotKeys.sent };
      await refetch({
        ...messageListVariables,
        filter: {
          _isOutgoingMessage,
        },
      });
    },
    [refetch, messageListVariables]
  );

  const loadMore = useCallback(async () => {
    await fetchMore({
      variables: {
        ...messageListVariables,
        after: messagesData?.invoice?.userMessagesByEntityId.pageInfo.endCursor,
      },
    });
  }, [fetchMore, messageListVariables, messagesData]);

  const data =
    networkStatus === NetworkStatus.setVariables
      ? undefined
      : messagesData?.invoice?.userMessagesByEntityId.nodes;

  return (
    <Stack className={styles.container}>
      <MessagesSections
        userMessageCounts={userMessageCounts}
        entityId={invoiceID}
        data={data || []}
        loadingMessages={loadingMessages}
        onListChange={onListChange}
        onLoadMore={loadMore}
        hasNextPage={
          messagesData?.invoice?.userMessagesByEntityId?.pageInfo?.hasNextPage
        }
      >
        <NewMessage
          onCreateMessage={onCreateMessage}
          selectedKey={selectedKey}
          invoiceID={invoiceID}
        />
      </MessagesSections>
    </Stack>
  );
};
